/**
 * events.js — the game's typed event bus.
 *
 * Every cross-module signal travels through this one object. Modules never
 * import each other just to call a method; they emit and listen. That is what
 * lets ten agents build ten subsystems in parallel without import cycles.
 *
 * Event name strings are defined in constants.js (EVENTS) and re-exported here
 * as `Events` for convenience.
 *
 *   import { bus, Events } from './core/events.js';
 *   const off = bus.on(Events.STALL_SCORE, ({ stallId, points }) => { ... });
 *   bus.emit(Events.STALL_SCORE, { stallId: 'tin-can-alley', points: 30 });
 *   off();   // unsubscribe
 *
 * @module core/events
 */

import { EVENTS } from '../constants.js';

export const Events = EVENTS;

class EventBus {
  constructor() {
    /** @type {Map<string, Set<Function>>} */
    this._handlers = new Map();
    /** Ring buffer of recent emissions, for the test-mode inspector. */
    this._log = [];
    this._logging = false;
    this._logLimit = 400;
  }

  /**
   * Subscribe to an event.
   * @param {string} name
   * @param {(payload: any) => void} fn
   * @returns {() => void} Unsubscribe function.
   */
  on(name, fn) {
    if (typeof fn !== 'function') throw new TypeError(`bus.on(${name}): handler must be a function`);
    let set = this._handlers.get(name);
    if (!set) {
      set = new Set();
      this._handlers.set(name, set);
    }
    set.add(fn);
    return () => this.off(name, fn);
  }

  /**
   * Subscribe for a single emission.
   * @param {string} name
   * @param {(payload: any) => void} fn
   * @returns {() => void} Unsubscribe function.
   */
  once(name, fn) {
    const off = this.on(name, (payload) => {
      off();
      fn(payload);
    });
    return off;
  }

  /**
   * Unsubscribe a specific handler.
   * @param {string} name
   * @param {Function} fn
   */
  off(name, fn) {
    const set = this._handlers.get(name);
    if (!set) return;
    set.delete(fn);
    if (set.size === 0) this._handlers.delete(name);
  }

  /**
   * Emit an event. Handler exceptions are caught and reported so one bad
   * listener cannot take down the frame loop.
   * @param {string} name
   * @param {any} [payload]
   */
  emit(name, payload) {
    if (this._logging) {
      this._log.push({ name, payload, t: performance.now() });
      if (this._log.length > this._logLimit) this._log.shift();
    }
    const set = this._handlers.get(name);
    if (!set || set.size === 0) return;
    // Iterate a copy: handlers commonly unsubscribe themselves mid-dispatch.
    for (const fn of [...set]) {
      try {
        fn(payload);
      } catch (err) {
        console.error(`[bus] handler for "${name}" threw:`, err);
        if (typeof window !== 'undefined' && Array.isArray(window.__ccErrors)) {
          window.__ccErrors.push(`bus handler ${name}: ${err && err.message}`);
        }
      }
    }
  }

  /**
   * Wait for the next emission of an event.
   * @param {string} name
   * @param {number} [timeoutMs]
   * @returns {Promise<any>}
   */
  next(name, timeoutMs = 0) {
    return new Promise((resolve, reject) => {
      const off = this.once(name, (payload) => {
        if (timer) clearTimeout(timer);
        resolve(payload);
      });
      const timer = timeoutMs > 0
        ? setTimeout(() => { off(); reject(new Error(`bus.next("${name}") timed out`)); }, timeoutMs)
        : null;
    });
  }

  /** @param {string} name @returns {number} Live handler count. */
  listenerCount(name) {
    const set = this._handlers.get(name);
    return set ? set.size : 0;
  }

  /** Drop every handler. Used by test-mode resets only. */
  clear() {
    this._handlers.clear();
  }

  /** Start recording emissions (test mode turns this on). */
  startLogging() {
    this._logging = true;
    this._log.length = 0;
  }

  /** @returns {Array<{name:string,payload:any,t:number}>} Recorded emissions. */
  getLog() {
    return this._log.slice();
  }
}

/** The one bus. */
export const bus = new EventBus();
